import { Cycle, Timer } from "~/types"
import { calculatePercentage } from "~/utils/TimerUtils"

const faviconElement = document.querySelector(
  'link[rel="icon"]',
) as HTMLLinkElement

export function setCycleFavicon(cycle: Cycle) {
  if (!faviconElement) return

  faviconElement.href =
    cycle === "break" ? "/favicon-break.png" : "/favicon.png"
}

export function setTimerFavicon(
  timer: Timer | undefined,
  progressScale = 1, // Used for scaling the timer in
) {
  if (!faviconElement || !timer) return

  const progress = calculatePercentage(timer) / 100
  const isBreak = timer.break
  const rendered = renderFavicon(isBreak, progress, progressScale)
  if (faviconElement.href !== rendered) faviconElement.href = rendered
}

let animateDuration: number | undefined
let animateTimer: Timer | undefined
let animateStartTime: number | undefined
let animateInCallback: (() => void) | undefined

export function animateTimerFaviconIn(timer: Timer, duration: number) {
  animateDuration = duration
  animateTimer = timer
  requestAnimationFrame(animateStep)
  return new Promise((resolve) => (animateInCallback = resolve as () => void))
}

function animateStep(time: number) {
  if (!animateDuration || !animateTimer) return

  if (!animateStartTime) animateStartTime = time

  const animateTimePassed = time - animateStartTime
  const d = Math.min(1, animateTimePassed / animateDuration) // Get the animation progress [0-1]

  const easeOutCubic = (d: number) => 1 - Math.pow(1 - d, 3)
  setTimerFavicon(
    animateTimer,
    easeOutCubic(d), // Apply an easing function to the progress to make the animation smooth
  )

  if (d !== 1) {
    requestAnimationFrame(animateStep)
  } else {
    animateInCallback?.()
    animateDuration = undefined
    animateTimer = undefined
    animateStartTime = undefined
    animateInCallback = undefined
  }
}

// Reuse canvas
const canvas = document.createElement("canvas")
const canvasSize = 64
const centerPoint = canvasSize / 2
const progressRadius = (canvasSize / 2 / 16) * 12 // 2 px margin in an imaginary 16 px wide favicon
canvas.height = canvasSize
canvas.width = canvasSize
const ctx = canvas.getContext("2d")

// Load png favicons to images (so that we can use them as canvas backgrounds)
const workIcon = new Image()
workIcon.src = "/favicon.png"
const breakIcon = new Image()
breakIcon.src = "/favicon-break.png"

const faviconCache: Record<string, string> = {}
function renderFavicon(
  isBreak: boolean,
  progress: number,
  progressScale: number,
) {
  if (!ctx) return isBreak ? "/favicon-break.png" : "/favicon.png"

  // Round progress and scale to a limited number of steps.
  // This prevents us from updating favicon more often than needed, and allows us to cache and reuse renders
  const progressSteps = 72 // 1 step per 5 degrees in a 360 degrees circle
  const scaleSteps = 15 // Browsers display very few favicon images per second, so we don't need many steps for animating the pie in
  const currentProgressStep = Math.round(progress * progressSteps)
  const currentScaleStep = Math.round(progressScale * scaleSteps)
  const cacheKey = [isBreak, currentProgressStep, currentScaleStep].join("-")

  if (faviconCache[cacheKey]) return faviconCache[cacheKey]

  // Round progress and scale to their "step"
  progress = currentProgressStep / progressSteps
  progressScale = currentScaleStep / scaleSteps

  ctx.clearRect(0, 0, canvasSize, canvasSize)

  // Draw normal favicon as background
  const usedBackground = isBreak ? breakIcon : workIcon
  ctx.drawImage(usedBackground, 0, 0, canvasSize, canvasSize)

  // Draw progress circle
  const circleStartingPoint = -Math.PI / 2
  const fullCircle = 2 * Math.PI
  ctx.beginPath()
  ctx.moveTo(centerPoint, centerPoint) // Start the path in the center
  ctx.arc(
    // Add the progress circle fragment to the path
    centerPoint,
    centerPoint,
    progressRadius * progressScale,
    circleStartingPoint + progress * fullCircle,
    circleStartingPoint + fullCircle,
  )
  ctx.lineTo(centerPoint, centerPoint) // Move back to the center
  ctx.fillStyle = isBreak ? "#ddf4e9" : "#f5c8c5"
  ctx.fill() // Fill the created path

  faviconCache[cacheKey] = canvas.toDataURL("image/png")
  return faviconCache[cacheKey]
}
